import { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'
import {
  createDonutChartOption,
  createHorizontalBarChartOption,
  createStackedBarChartOption,
  createRadarChartOption,
} from '../../utils/chartUtils'
import { useLocation } from '../../contexts/LocationContext'

function HealthCharts({ chartData, filtered }) {
  const { isViewingAllDistricts } = useLocation()

  // Chart options
  const donutChartOption = useMemo(() => {
    if (!chartData) return null
    return createDonutChartOption(
      chartData.facilityLevels,
      'Healthcare Access Overview',
      `${filtered}\nfacilities`
    )
  }, [chartData, filtered])

  const barChartOption = useMemo(() => {
    if (!chartData) return null
    return createHorizontalBarChartOption(
      chartData.serviceCoverage,
      'Service Coverage Gap Analysis'
    )
  }, [chartData])

  const radarChartOption = useMemo(() => {
    if (!chartData) return null
    return createRadarChartOption(
      chartData.infrastructureReadiness,
      'Infrastructure Readiness Score'
    )
  }, [chartData])

  const stackedBarChartOption = useMemo(() => {
    if (!chartData) return null
    return createStackedBarChartOption(
      chartData.staffingAnalysis,
      'Healthcare Staffing Distribution'
    )
  }, [chartData])

  if (!chartData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Loading health analytics...</p>
      </div>
    )
  }

  return (
    <>
      {/* Healthcare Access Overview - Full Width */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          Healthcare Access Overview
        </h3>
        <div className="h-80">
          {donutChartOption && (
            <ReactECharts
              option={donutChartOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          )}
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p className="font-medium">What level of care is available?</p>
          <p className="mt-2">
            {isViewingAllDistricts()
              ? 'National distribution shows the overall healthcare infrastructure across all districts.'
              : 'Distribution shows if the area has only basic care or includes referral facilities.'}
          </p>
        </div>
      </div>

      {/* Infrastructure Readiness Score - Full Width */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          Infrastructure Readiness Score
        </h3>
        <div className="h-80">
          {radarChartOption && (
            <ReactECharts
              option={radarChartOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          )}
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p className="font-medium">How equipped are facilities?</p>
          <p className="mt-2">
            Infrastructure readiness shows the percentage of facilities with
            essential infrastructure.
          </p>
        </div>
      </div>

      {/* Service Coverage Gap Analysis - Full Width Bar Chart */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          Service Coverage Gap Analysis
        </h3>
        <div className="h-96">
          {barChartOption && (
            <ReactECharts
              option={barChartOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          )}
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p className="font-medium">Which services need more coverage?</p>
          <p className="mt-2">
            Gaps in service coverage can indicate areas for investment and
            improvement in healthcare delivery.
          </p>
        </div>
      </div>

      {/* Healthcare Worker Distribution */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          Healthcare Staffing Distribution
        </h3>
        <div className="h-96">
          {stackedBarChartOption && (
            <ReactECharts
              option={stackedBarChartOption}
              style={{ height: '100%', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              theme="ug-dashboard"
            />
          )}
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <p className="font-medium">
            Do we have the right mix of healthcare workers?
          </p>
          <p className="mt-2">
            This chart shows the distribution of different staff categories
            across all facilities, helping identify staffing gaps and strengths.
          </p>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          📈 Summary Statistics
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600">
              {filtered}
            </div>
            <div className="text-sm text-gray-600">Total Facilities</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {chartData?.facilityLevels.length || 0}
            </div>
            <div className="text-sm text-gray-600">Facility Types</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {chartData?.serviceCoverage.filter(s => s.percentage >= 80)
                .length || 0}
            </div>
            <div className="text-sm text-gray-600">Well-Covered Services</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {chartData?.supplyChainStatus.filter(s => s.status === 'good')
                .length || 0}
            </div>
            <div className="text-sm text-gray-600">Adequate Supplies</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {chartData?.staffingAnalysis.grandTotal || 0}
            </div>
            <div className="text-sm text-gray-600">Healthcare Workers</div>
          </div>
        </div>
      </div>
    </>
  )
}

HealthCharts.propTypes = {
  chartData: PropTypes.shape({
    facilityLevels: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
        length: PropTypes.number,
      })
    ),
    serviceCoverage: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        percentage: PropTypes.number,
        filter: PropTypes.func,
      })
    ),
    infrastructureReadiness: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
      })
    ),
    supplyChainStatus: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        status: PropTypes.string,
        filter: PropTypes.func,
      })
    ),
    staffingAnalysis: PropTypes.shape({
      grandTotal: PropTypes.number,
    }),
  }),
  filtered: PropTypes.number,
}

export default HealthCharts
